import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { verifyEngageDomainDNS as verifyEngageDomainDNSApi } from 'api/engage';
import { GivebutterFormProvider, SubmitButton, useGivebutterForm } from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageDomain } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const DNSVerification = (props: { domain: EngageDomain }) => {
    const toast = useToast();
    const { domain } = props;
    const { accountId } = useCurrentAccount();
    const methods = useGivebutterForm();
    const { handleSubmit, reset } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: () => verifyEngageDomainDNSApi(accountId, domain.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageDomains(accountId),
            });
            toast.success(<>Successfully refreshed domain verification</>);
        },
        onError: (error: GivebutterAxiosError) => {
            reset();
            toast.error(
                <>
                    {error.response?.data.message ?? 'Unknown error refreshing domain verification'}
                </>,
            );
        },
    });
    return (
        <div className="flex w-full items-center justify-between">
            <GivebutterFormProvider {...methods}>
                <form onSubmit={handleSubmit(() => submit())}>
                    <SubmitButton className="mr-3 !inline !rounded-lg !border !border-gray-300 !bg-white px-3 py-1.5 !text-sm !font-medium !text-gray-900">
                        <FontAwesomeIcon icon={faRefresh} className="mr-1.5" />
                        Refresh Status
                    </SubmitButton>
                    <span className="text-xs italic text-gray-500">
                        Last updated:{' '}
                        {domain.last_checked_at
                            ? DateTime.fromISO(domain.last_checked_at).toLocaleString(
                                  DateTime.DATETIME_MED,
                              )
                            : 'never'}
                    </span>
                </form>
            </GivebutterFormProvider>
            {!domain.is_subdomain && (
                <a
                    href="https://help.givebutter.com/en/articles/5497885-how-to-connect-your-domain-to-send-emails-through-engage"
                    className="text-sm font-medium"
                    target="_blank"
                    rel="noreferrer"
                >
                    Need help?
                </a>
            )}
        </div>
    );
};

export { DNSVerification };
